import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Loader, CheckCircle, MessageCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useInstances } from '../contexts/InstancesContext';

const META_APP_ID = process.env.REACT_APP_META_APP_ID;
const META_CONFIG_ID = process.env.REACT_APP_META_CONFIG_ID;

const EmbeddedSignup = ({ clienteId, usuarioId, onSuccess, onClose, mode = 'normal' }) => {
  const { darkMode } = useTheme();
  const { refreshInstances } = useInstances();
  const [processing, setProcessing] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);

  useEffect(() => {
    // Init Facebook SDK
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v25.0'
      });
      setSdkReady(true);
    };

    // If SDK already loaded
    if (window.FB) {
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v25.0'
      });
      setSdkReady(true);
    }
  }, []);

  const sessionInfoListener = useCallback((event) => {
    if (!event.data) return;

    // Parse message from Embedded Signup
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        if (data.event === 'FINISH' || data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
          // User completed signup
          console.log(`Embedded Signup ${data.event}:`, data.data);
        } else if (data.event === 'CANCEL') {
          toast.info('Conexão cancelada');
          onClose?.();
        } else if (data.event === 'ERROR') {
          console.error('Embedded Signup error:', data.data);
          toast.error('Erro no processo de conexão');
        }
      }
    } catch (e) {
      // Not a JSON message, ignore
    }
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('message', sessionInfoListener);
    return () => window.removeEventListener('message', sessionInfoListener);
  }, [sessionInfoListener]);

  const isCoexistence = mode === 'coexistence';

  const launchEmbeddedSignup = () => {
    if (!window.FB) {
      toast.error('Facebook SDK não carregou. Recarregue a página.');
      return;
    }

    window.FB.login(
      (response) => {
        if (response.authResponse) {
          const code = response.authResponse.code;
          console.log('Embedded Signup code received:', code);
          processSignup(code);
        } else {
          toast.info('Conexão cancelada');
        }
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: isCoexistence
          ? {
              setup: {},
              featureType: 'whatsapp_business_app_onboarding',
              sessionInfoVersion: '3',
            }
          : {
              setup: {
                business: {
                  id: '278173086130476',
                },
                waba: {
                  id: '962195006132599',
                },
                phone: {},
              },
              featureType: 'only_waba_sharing',
              sessionInfoVersion: '3',
            },
      }
    );
  };

  const processSignup = async (code) => {
    setProcessing(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/meta-auth/embedded-signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            code,
            cliente_id: clienteId,
            usuario_id: usuarioId,
            is_coexistence: isCoexistence,
          }),
        }
      );

      const result = await res.json();

      if (result.success) {
        const modeLabel = isCoexistence ? 'WhatsApp conectado (modo híbrido — app + API)' : 'WhatsApp conectado';
        toast.success(`${modeLabel}: ${result.data.phone_number || 'Número registrado'}`);
        await refreshInstances();
        onSuccess?.(result.data);
      } else {
        toast.error(result.error || 'Erro ao processar conexão');
      }
    } catch (error) {
      console.error('Erro ao processar Embedded Signup:', error);
      toast.error('Erro ao conectar WhatsApp');
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader className="w-8 h-8 animate-spin text-green-500" />
        <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
          Processando conexão do WhatsApp...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-6 py-8">
      <div className="text-center space-y-2">
        <MessageCircle className="w-12 h-12 text-green-500 mx-auto" />
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {isCoexistence ? 'Conectar número existente (App + API)' : 'Conectar WhatsApp Business'}
        </h3>
        <p className={`text-sm max-w-md ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {isCoexistence
            ? 'Conecte seu número do WhatsApp Business App à plataforma Nomoz mantendo o app funcionando no celular. Você poderá usar os dois ao mesmo tempo.'
            : 'Conecte seu número WhatsApp Business à plataforma Nomoz. Você será guiado pelo processo de configuração da Meta.'
          }
        </p>
        {isCoexistence && (
          <div className={`mt-3 p-3 rounded-lg text-xs ${darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-50 text-yellow-700'}`}>
            <strong>Limitações:</strong> 20 msg/seg • Grupos não sincronizam • App precisa ser aberto 1x a cada 14 dias
          </div>
        )}
      </div>

      <button
        onClick={launchEmbeddedSignup}
        disabled={!sdkReady}
        className="flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
      >
        <MessageCircle className="w-5 h-5" />
        <span>{sdkReady ? (isCoexistence ? 'Conectar com App + API' : 'Conectar WhatsApp') : 'Carregando...'}</span>
      </button>

      {!sdkReady && (
        <p className="text-xs text-gray-500">Aguardando Facebook SDK carregar...</p>
      )}
    </div>
  );
};

export default EmbeddedSignup;
